import React from 'react';
import { Sparkles, BookOpen, Users, CheckCircle, ArrowRight } from 'lucide-react';
import { trackStudentCTA, trackTeacherCTA } from '../../services/analytics';

const Hero = ({ 
  isDarkMode, 
  isTrialActive, 
  trialEmail, 
  user, 
  onGetStarted 
}) => {
  const handleStudentClick = () => {
    trackStudentCTA('hero');
    onGetStarted('student');
  };

  const handleTeacherClick = () => {
    trackTeacherCTA('hero');
    onGetStarted('teacher');
  };

  return (
    <section data-testid="section-hero" className="py-16 md:py-24 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute -top-40 -right-40 w-80 h-80 rounded-full blur-3xl ${isDarkMode ? 'bg-purple-900/30' : 'bg-purple-200/50'}`} />
        <div className={`absolute -bottom-40 -left-40 w-80 h-80 rounded-full blur-3xl ${isDarkMode ? 'bg-violet-900/30' : 'bg-violet-200/50'}`} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center max-w-4xl mx-auto">
          {/* Badge */}
          <div className="flex justify-center mb-6">
            <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.2em] ${isDarkMode ? 'bg-purple-900/50 text-purple-300 border border-purple-700/50' : 'bg-purple-100 text-purple-700 border border-purple-200'}`}>
              <Sparkles className="w-4 h-4" />
              For Students & Teachers
            </span>
          </div>

          {/* Main Heading */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Outfit'] tracking-tight leading-tight mb-6">
            Turn Study Materials Into{' '}
            <span className="bg-gradient-to-r from-purple-500 via-violet-500 to-purple-600 bg-clip-text text-transparent">
              Active Practice
            </span>
          </h1>

          {/* Subheading */}
          <p className={`text-base sm:text-lg lg:text-xl leading-relaxed mb-8 max-w-3xl mx-auto ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
            Whether you're a student testing your knowledge or a teacher finding out what your students know, AIQuizPark converts PDFs, images, notes, audio, and videos into interactive smart quizzes in seconds.
          </p>

          {/* Positioning Statements */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-10">
            <div className={`flex items-center gap-3 px-5 py-3 rounded-xl ${isDarkMode ? 'bg-sky-900/30 border border-sky-700/50' : 'bg-sky-50 border border-sky-200'}`}>
              <BookOpen className={`w-5 h-5 ${isDarkMode ? 'text-sky-400' : 'text-sky-600'}`} />
              <span className={`text-sm font-semibold ${isDarkMode ? 'text-sky-300' : 'text-sky-700'}`}>Students: Find out what you know</span>
            </div>
            <div className={`flex items-center gap-3 px-5 py-3 rounded-xl ${isDarkMode ? 'bg-emerald-900/30 border border-emerald-700/50' : 'bg-emerald-50 border border-emerald-200'}`}>
              <Users className={`w-5 h-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
              <span className={`text-sm font-semibold ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>Teachers: Find out what your students know</span>
            </div>
          </div>

          {/* Dual CTAs */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
            <button
              onClick={handleStudentClick}
              data-testid="hero-cta-student"
              className="group px-8 py-4 bg-gradient-to-r from-sky-500 to-sky-600 text-white rounded-xl font-bold text-lg hover:-translate-y-1 active:translate-y-0 transition-all shadow-xl shadow-sky-500/30 flex items-center justify-center gap-2"
            >
              I'm a Student
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={handleTeacherClick}
              data-testid="hero-cta-teacher"
              className="group px-8 py-4 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl font-bold text-lg hover:-translate-y-1 active:translate-y-0 transition-all shadow-xl shadow-emerald-500/30 flex items-center justify-center gap-2"
            >
              I'm a Teacher
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          {/* Trial Status */}
          {isTrialActive && !user && (
            <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm ${isDarkMode ? 'bg-green-900/30 text-green-400 border border-green-700/50' : 'bg-green-50 text-green-700 border border-green-200'}`}>
              <CheckCircle className="w-4 h-4 mr-2" />
              Trial active for {trialEmail}
            </div>
          )}

          {/* Quick Stats */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: '50K+', label: 'Quizzes Generated' },
              { value: '15+', label: 'Exam Presets' },
              { value: '5+', label: 'Input Formats' },
              { value: '99%', label: 'Accuracy Rate' }
            ].map((stat, i) => (
              <div key={i} className={`p-4 rounded-xl ${isDarkMode ? 'bg-slate-800/50 border border-slate-700' : 'bg-white border border-slate-200'}`}>
                <div className="text-2xl sm:text-3xl font-black font-['Outfit'] bg-gradient-to-r from-purple-500 to-violet-500 bg-clip-text text-transparent">{stat.value}</div>
                <div className={`text-xs sm:text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
